<template>
  <v-container>
    <v-layout row wrap>
      <v-flex xs12>
        <v-alert
          v-model="isError"
          dismissible
          type="error"
          transition="fade-transition"
        >
          {{ errMessage }}
        </v-alert>
      </v-flex>
      <v-flex xs12>
        <h1>Church Management</h1>
      </v-flex>
      <v-flex xs12>
        <v-card class="my-2">
          <v-card-title>
            <h2 class="headline">Church Information</h2>
          </v-card-title>
          <v-card-text>
            <p>Name: {{ church.name || 'Loading...' }}</p>
            <p>City: {{ church.city || 'Loading...' }}</p>
            <p>State: {{ church.state || 'Loading...' }}</p>
            <p>Approved for Listing? {{ church.approvedAt ? 'Yes' : 'No' }}</p>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12>
        <v-card class="my-2">
          <v-card-title>
            <h2 class="headline">Subscription</h2>
          </v-card-title>
          <v-card-text>
            <p v-if="subscriptionEnd">Active until {{ subscriptionEnd }}</p>
            <p v-else>Not active</p>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12>
        <v-card class="my-2">
          <v-card-title>
            <h2 class="headline">Staff</h2>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="staffHeaders"
              :items="staff"
            >
              <template slot="items" slot-scope="props">
                <td>{{ props.item.email || 'Loading...' }}</td>
                <td>{{ props.item.approvedAt || 'N/A' }}</td>
                <td>
                  <v-icon
                    small
                    @click="approveStaff(props.item)"
                    v-if="!props.item.approvedAt"
                  >thumb_up
                  </v-icon>
                </td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex xs12>
        <v-card class="my-2">
          <v-card-title>
            <h2 class="headline">Members</h2>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 text-xs-center>
                <h3>Devotional Activity</h3>
              </v-flex>
              <v-flex xs4 text-xs-center>
                <v-progress-circular
                  :value="$root.devotionalCompletionsInWeek / (7 * members.length) * 100"
                  size="60"
                  color="primary"
                >
                  {{ $root.devotionalCompletionsInWeek }}/{{ 7 * members.length }}
                </v-progress-circular>
                <p>Week</p>
              </v-flex>
              <v-flex xs4 text-xs-center>
                <v-progress-circular
                  :value="$root.devotionalCompletionsInMonth / (daysInMonth * members.length) * 100"
                  size="60"
                  color="primary"
                >
                  {{ $root.devotionalCompletionsInMonth }}/{{ daysInMonth * members.length }}
                </v-progress-circular>
                <p>Month</p>
              </v-flex>
              <v-flex xs4 text-xs-center>
                <v-progress-circular
                  :value="$root.devotionalCompletionsInYear / (daysInYear * members.length) * 100"
                  size="60"
                  color="primary"
                >
                  {{ $root.devotionalCompletionsInYear }}/{{ daysInYear * members.length }}
                </v-progress-circular>
                <p>Year</p>
              </v-flex>
            </v-layout>
            <v-data-table
              :headers="memberHeaders"
              :items="members"
            >
              <template slot="items" slot-scope="props">
                <td>{{ props.item.name }}</td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
const { DateTime } = require('luxon')

export default {
  data: () => ({
    isError: false,
    errMessage: null,
    church: {
      id: null,
      name: null,
      city: null,
      state: null,
      approvedAt: null
    },
    subscriptionEnd: null,
    staff: [],
    staffHeaders: [
      { text: 'Email', value: 'email' },
      { text: 'Approved Since', value: 'approvedAt' },
      { text: 'Actions', value: 'email' } // no actual value
    ],
    members: [],
    memberHeaders: [
      { text: 'Name', value: 'name' }
    ]
  }),
  computed: {
    daysInMonth: function () {
      return DateTime.local().daysInMonth
    },
    daysInYear: function () {
      return DateTime.local().daysInYear
    }
  },
  mounted: function () {
    this.$root.updateDevotionalCompletions()

    let _this = this
    let user = this.$auth.user()

    // If we have an ID in our URL, use it; otherwise, use the one from our user
    if (this.$route.params.id) {
      this.church.id = this.$route.params.id
    } else {
      this.church.id = user.churchID
    }

    // Get information about our church
    this.axios.get('/churches/' + this.church.id)
      .then(function (res) {
        // Sanity check before assigning the value
        if (res.data.length > 0) {
          _this.church = res.data[0]
        } else {
          throw new RangeError('No church information returned')
        }
      })
      .catch(this.$root.apiErrorHandler(function (msg, errors, err) {
        // If our error is a 404, that means we got a bogus church ID value and
        // we can safely abort back to the management page. Otherwise, we want
        // to render our error message.
        if (err && err.response && err.response.status === 404) {
          return _this.$router.push('/manage')
        } else {
          _this.errMessage = msg
          _this.isError = true
        }
      }))

    // Get information about our church's subscription(s)
    this.axios.get('/subscriptions/church/' + this.church.id)
      .then(function (res) {
        // It's possible to have multiple subscription records; we want to find
        // the latest one so we know our ultimate end date
        let date = null
        for (let i = 0; i < res.data.length; i++) {
          let sub = res.data[i]
          if (date === null ||
              (date !== null && sub.endDate > date)) {
            date = sub.endDate
          }
        }
        _this.subscriptionEnd = date
      })
      .catch(this.$root.apiErrorHandler())

    // Get information about our church's staff and members
    this.axios.get('/auth/users')
      .then(function (res) {
        // Build out information about church-staff
        let staff = res.data.filter(function (user) {
          return (user.roles === 'church-staff') &&
            (user.churchID === _this.church.id)
        })
        _this.staff = staff.map(function (user) {
          // Make a simplified email field
          let email
          if (user.local.email) {
            email = user.local.email
          } else if (user.google.email) {
            email = user.google.email
          } else if (user.facebook.email) {
            email = user.facebook.email
          } else {
            email = 'N/A'
          }
          user.email = email

          return user
        })

        // Build out information about church-members
        let members = res.data.filter(function (user) {
          return (user.roles === 'church-member') &&
            (user.churchID === _this.church.id)
        })
        _this.members = members.map(function (user) {
          // Make a combined name field
          user.name = user.firstName + ' ' + user.lastName

          return user
        })
      })
      .catch(this.$root.apiErrorHandler())
  },
  methods: {
    approveStaff: function (s) {
      // Confirm with the user that they want to perform the requested action
      // before actually making the request
      var _this = this
      confirm('Do you want to approve this user to manage your church?') &&
      this.axios.post('/approvals/church-staff/' + this.church.id + '/' + s.id)
        .then(function (res) {
          // Update the approval value in our data
          s.approvedAt = res.data.approvedAt
        })
        .catch(_this.$root.apiErrorHandler(function (msg) {
          _this.errMessage = msg
          _this.isError = true
        }))
    }
  }
}
</script>
